import { useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import {
    useGetAllErrors,
    useCreateError,
    useUpdateError,
    useDeleteError,
    ErrorDefinition,
} from "../../../api/routes/errors";
import { authClient, hasPermission } from "../../../lib/auth-client";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../../components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../../../components/ui/dialog";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";
import { toast } from "react-toastify";

const emptyForm = { code: "", title: "", description: "", number: 0 };

export const ErrorDefinitionsRoute = () => {
    const { id: modelId } = useParams<{ id: string }>();
    const { data: errors, isLoading } = useGetAllErrors(modelId);
    const createError = useCreateError(modelId);
    const updateError = useUpdateError(modelId);
    const deleteError = useDeleteError(modelId);

    const { data: session } = authClient.useSession();
    const role = (session?.user as any)?.role;
    const canConfigure = role && hasPermission(role, { model: ["configure"] });

    const [filter, setFilter] = useState("");
    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [selected, setSelected] = useState<ErrorDefinition | null>(null);
    const [form, setForm] = useState(emptyForm);

    const filtered = useMemo(() => {
        if (!errors) return [];
        if (!filter) return errors;
        const q = filter.toLowerCase();
        return errors.filter(
            (e) =>
                e.code.toLowerCase().includes(q) ||
                e.title.toLowerCase().includes(q)
        );
    }, [errors, filter]);

    const openCreate = () => {
        setForm(emptyForm);
        setCreateOpen(true);
    };

    const openEdit = (err: ErrorDefinition) => {
        setSelected(err);
        setForm({
            code: err.code,
            title: err.title,
            description: err.description,
            number: err.number,
        });
        setEditOpen(true);
    };

    const openDelete = (err: ErrorDefinition) => {
        setSelected(err);
        setDeleteOpen(true);
    };

    const handleCreate = (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.code.trim() || !form.title.trim()) {
            toast.error("Code and title are required");
            return;
        }
        createError.mutate(form, {
            onSuccess: () => {
                toast.success("Error definition created");
                setCreateOpen(false);
            },
            onError: (error: any) => {
                toast.error(error?.response?.data?.message || error.message || "Failed to create error definition");
            },
        });
    };

    const handleEdit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!selected) return;
        updateError.mutate(
            { id: selected.id, data: form },
            {
                onSuccess: () => {
                    toast.success("Error definition updated");
                    setEditOpen(false);
                },
                onError: (error: any) => {
                    toast.error(error?.response?.data?.message || error.message || "Failed to update error definition");
                },
            }
        );
    };

    const handleDelete = () => {
        if (!selected) return;
        deleteError.mutate(selected.id, {
            onSuccess: () => {
                toast.success("Error definition deleted");
                setDeleteOpen(false);
            },
            onError: (error: any) => {
                toast.error(error?.response?.data?.message || error.message || "Failed to delete error definition");
            },
        });
    };

    const formFields = (onSubmit: (e: React.FormEvent) => void, isPending: boolean, submitLabel: string) => (
        <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Number</label>
                    <input
                        type="number"
                        value={form.number}
                        onChange={(e) => setForm({ ...form, number: Number(e.target.value) })}
                        required
                        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                    />
                </div>
                <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Code</label>
                    <input
                        value={form.code}
                        onChange={(e) => setForm({ ...form, code: e.target.value })}
                        required
                        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                    />
                </div>
            </div>
            <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Title</label>
                <input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    required
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                />
            </div>
            <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Description</label>
                <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    required
                    rows={3}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring resize-none"
                />
            </div>
            <DialogFooter className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => { setCreateOpen(false); setEditOpen(false); }}>
                    Cancel
                </Button>
                <Button type="submit" disabled={isPending}>
                    {isPending ? "Saving..." : submitLabel}
                </Button>
            </DialogFooter>
        </form>
    );

    if (isLoading) {
        return <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">Loading...</div>;
    }

    return (
        <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
            {modelId && (
                <div className="mb-6">
                    <Link
                        to={`/models/${modelId}`}
                        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Model
                    </Link>
                </div>
            )}
            <div className="flex items-center justify-between py-4">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight text-foreground">Error Definitions</h1>
                </div>
                <div className="flex items-center gap-3">
                    <Input
                        placeholder="Filter by code or title"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="max-w-sm"
                    />
                    {canConfigure && (
                        <Button variant="default" onClick={openCreate}>
                            Add Error Definition
                        </Button>
                    )}
                </div>
            </div>
            <div className="rounded-md border w-full bg-white">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Number</TableHead>
                            <TableHead>Code</TableHead>
                            <TableHead>Title</TableHead>
                            <TableHead>Description</TableHead>
                            {canConfigure && (
                                <TableHead className="w-[100px]">
                                    <span className="sr-only">Actions</span>
                                </TableHead>
                            )}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {filtered.length > 0 ? (
                            filtered.map((err) => (
                                <TableRow key={err.id}>
                                    <TableCell>{err.number}</TableCell>
                                    <TableCell className="font-mono text-xs">{err.code}</TableCell>
                                    <TableCell>{err.title}</TableCell>
                                    <TableCell className="max-w-xs truncate">{err.description}</TableCell>
                                    {canConfigure && (
                                        <TableCell>
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={() => openEdit(err)}
                                                    className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                                >
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => openDelete(err)}
                                                    className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={canConfigure ? 5 : 4}>No results.</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Create Dialog */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Add Error Definition</DialogTitle>
                    </DialogHeader>
                    {formFields(handleCreate, createError.isPending, "Create")}
                </DialogContent>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Edit Error Definition</DialogTitle>
                    </DialogHeader>
                    {formFields(handleEdit, updateError.isPending, "Save Changes")}
                </DialogContent>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Delete Error Definition</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-sm text-foreground">
                            Are you sure you want to permanently delete{" "}
                            <span className="font-semibold">{selected?.code} — {selected?.title}</span>?
                            This action cannot be undone.
                        </p>
                    </div>
                    <DialogFooter className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setDeleteOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDelete} disabled={deleteError.isPending}>
                            {deleteError.isPending ? "Deleting..." : "Delete"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
